/**
 * Stop-level printable timetable poster dialog (milestone M1).
 *
 * Flow: choose a stop (from the route the operator started on) → preview the UK
 * council poster (aggregating every service that calls at the stop) → print/save
 * as PDF via the browser print dialog (A4 portrait).
 *
 * ponytail: M1 ships one template (UK) + A4 portrait, browser print. Paper sizes,
 * branding-from-config, metadata editing and the London template land in M2–M4.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Icon } from '@liftango/ui-icons';

import type { FixedRoute, FixedRouteStop } from '../../../types/fixedRoutes';
import { buildStopPosterData } from '../../../utils/fixedRoutes/stopPosterData';
import {
  POSTER_TEMPLATES,
  DEFAULT_POSTER_TEMPLATE_ID,
  getPosterTemplate,
  type PosterTemplateId,
} from './posterTemplates';
import './posterPrint.css';

interface StopPosterDialogProps {
  open: boolean;
  onClose: () => void;
  /** Route the operator started from — provides the stop list to choose from. */
  route: FixedRoute | null;
  /** All routes, so the poster shows every service calling at the chosen stop. */
  routes: FixedRoute[];
  /** All stops, for name/ATCO/NaPTAN lookup. */
  stops: FixedRouteStop[];
}

const PREVIEW_SCALE = 0.62;

function todayLabel(): string {
  return new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export function StopPosterDialog({ open, onClose, route, routes, stops }: StopPosterDialogProps) {
  const routeStops = useMemo(() => {
    if (!route) return [];
    return route.stops
      .map((sc) => stops.find((s) => s.id === sc.stopId))
      .filter((s): s is FixedRouteStop => Boolean(s));
  }, [route, stops]);

  const [selectedStopId, setSelectedStopId] = useState<string>('');
  const [validFrom, setValidFrom] = useState<string>(todayLabel());
  const [templateId, setTemplateId] = useState<PosterTemplateId>(DEFAULT_POSTER_TEMPLATE_ID);

  // Default the chosen stop to the route's first stop whenever the route changes.
  const effectiveStopId = selectedStopId || routeStops[0]?.id || '';

  const posterData = useMemo(() => {
    if (!effectiveStopId) return null;
    return buildStopPosterData(effectiveStopId, routes, stops);
  }, [effectiveStopId, routes, stops]);

  // ponytail: no locality/area line for M1 — the poster is stop-centric and the
  // route name is redundant with the stop headline. M2 adds an editable "Area" field.
  const handlePrint = () => window.print();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Icon name="file-arrow-down" size={20} />
        Print stop-level timetable
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 2 }}>
          {/* Controls */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="poster-stop-label">Choose a stop</InputLabel>
              <Select
                labelId="poster-stop-label"
                label="Choose a stop"
                value={effectiveStopId}
                onChange={(e) => setSelectedStopId(e.target.value)}
              >
                {routeStops.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              size="small"
              fullWidth
              label="Valid from"
              value={validFrom}
              onChange={(e) => setValidFrom(e.target.value)}
            />

            <FormControl size="small" fullWidth>
              <InputLabel id="poster-template-label">Template</InputLabel>
              <Select
                labelId="poster-template-label"
                label="Template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value as PosterTemplateId)}
              >
                {POSTER_TEMPLATES.map((tpl) => (
                  <MenuItem key={tpl.id} value={tpl.id}>
                    {tpl.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth disabled>
              <InputLabel id="poster-size-label">Paper size</InputLabel>
              <Select labelId="poster-size-label" label="Paper size" value="a4p">
                <MenuItem value="a4p">A4 portrait</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="caption" color="text.secondary">
              More paper sizes are coming. Print, then choose “Save as PDF” in your browser’s print
              dialog.
            </Typography>
          </Box>

          {/* Preview */}
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 1,
              p: 2,
              maxHeight: 620,
              overflow: 'auto',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {posterData ? (
              <div className="poster-print-root">
                <div className="poster-scale" style={{ zoom: PREVIEW_SCALE }}>
                  {(() => {
                    const Template = getPosterTemplate(templateId).Component;
                    return <Template data={posterData} validFrom={validFrom} />;
                  })()}
                </div>
              </div>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ m: 4 }}>
                Select a stop to preview its poster.
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button
          variant="contained"
          startIcon={<Icon name="file-arrow-down" size={18} />}
          onClick={handlePrint}
          disabled={!posterData || posterData.columns.length === 0}
        >
          Print / Save as PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StopPosterDialog;
